import Wrapper from './wrapper';
import {
  IExtractionListParams,
  IExtractionListResponse,
  ITransformParams,
  ITransformJsonResponse,
  TransformParamsCsv,
  TransformParamsJson,
  IWrapper,
  ApiError,
} from './types';
import { IExtraction } from '../types/my-types';

export const VALIDATION_NOT_READY_MESSAGE = 'Validation results are not ready yet.';
export const VALIDATION_POLL_INTERVAL_MS = 2000;
export const VALIDATION_POLL_BUDGET_MS = 5 * 60 * 1000;

/**
 * Thrown when the validation poll budget runs out while the API is still
 * answering 202. Callers must treat this as "no result", never as a clean
 * validation.
 */
export class ValidationNotReadyError extends Error {
  extractionId: string;

  constructor(extractionId: string) {
    super(
      `Validation results for extraction ${extractionId} were not ready within the poll budget.`
    );
    this.name = 'ValidationNotReadyError';
    this.extractionId = extractionId;
  }
}

/**
 * Thrown when the validate endpoint answers 400 `{"non_field_errors": [...]}`,
 * which it does once the extraction's results chain has given up its retries.
 * The message is the API's, naming the failed chain and how to rerun it.
 * Retrying cannot help until the chain is rerun.
 */
export class ResultsChainGaveUpError extends Error {
  extractionId: string;

  constructor(extractionId: string, message: string) {
    super(message);
    this.name = 'ResultsChainGaveUpError';
    this.extractionId = extractionId;
  }
}

const resultsChainGaveUpMessage = (error: unknown): string | undefined => {
  const { status, responseData } = (error ?? {}) as ApiError;
  const messages = (responseData as { non_field_errors?: unknown } | undefined)?.non_field_errors;
  if (status === 400 && Array.isArray(messages) && messages.length > 0) {
    return messages.map(String).join(' ');
  }
  return undefined;
};

class Extraction {
  wrapper: IWrapper;
  /**
   * @param {Wrapper} wrapper
   */
  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }
  retrieve(id: string) {
    return this.wrapper.client.get<IExtraction, IExtraction>(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/?advanced=true`
    );
  }
  retrieve_corrected_results(id: string) {
    return this.wrapper.client.get<IExtraction, IExtraction>(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/corrected-results/?advanced=true`
    );
  }
  retrieve_normalized_results(id: string) {
    return this.wrapper.client.get<IExtraction, IExtraction>(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/normalized-results/?advanced=true`
    );
  }
  retrieve_raw_result(id: string) {
    return this.wrapper.client.get<IExtraction, IExtraction>(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/raw-results/?advanced=true`
    );
  }
  del(id: string) {
    return this.wrapper.client.delete(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/?advanced=true`
    );
  }
  list(params: IExtractionListParams) {
    return this.wrapper.client.get<IExtractionListResponse, IExtractionListResponse>(
      `data-pools/${this.wrapper.dataPoolId}/extractions/`,
      { params }
    );
  }
  transformer(id: string, params: TransformParamsCsv): Promise<string>;
  transformer(id: string, params: TransformParamsJson): Promise<ITransformJsonResponse>;
  transformer(id: string, params: ITransformParams) {
    const accept = params.format === 'csv' ? 'text/csv' : 'application/json';
    const url = `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/transformed-results/`;
    if (params.format === 'csv') {
      return this.wrapper.client.get<string, string>(url, { params, headers: { accept } });
    }
    return this.wrapper.client.get<ITransformJsonResponse, ITransformJsonResponse>(url, {
      params,
      headers: { accept },
    });
  }
  // copy to training set
  copy(id: string, trainingSetId: string) {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/copy-as-training-data/?advanced=true`,
      { training_set: trainingSetId }
    );
  }
  async validate(
    id: string,
    extracted: boolean = true,
    transformed: boolean = false,
    forceValidation: boolean = false,
    schematized: boolean = false
  ) {
    let url = `data-pools/${this.wrapper.dataPoolId}/extractions/${id}/validate/?advanced=true`;
    url += `&extracted=${extracted}`;
    url += `&transformed=${transformed}`;
    if (schematized) {
      url += '&schematized=true';
    }

    // A forced validate without sync=true always answers 202 (the API marks the
    // datasets PENDING and queues a run), so force on the first request only and
    // poll plainly afterwards: 202 while PENDING, 200 once the run has settled.
    const firstUrl = forceValidation ? `${url}&force_validate=true` : url;

    const request = async (requestUrl: string) => {
      try {
        return await this.wrapper.client.get<any, any>(requestUrl);
      } catch (error) {
        const gaveUpMessage = resultsChainGaveUpMessage(error);
        throw gaveUpMessage ? new ResultsChainGaveUpError(id, gaveUpMessage) : error;
      }
    };

    const deadline = Date.now() + VALIDATION_POLL_BUDGET_MS;
    let response = await request(firstUrl);

    while (response === VALIDATION_NOT_READY_MESSAGE) {
      if (Date.now() >= deadline) {
        throw new ValidationNotReadyError(id);
      }
      await new Promise((resolve) => setTimeout(resolve, VALIDATION_POLL_INTERVAL_MS));
      response = await request(url);
    }

    return response;
  }
  verify(id: string, isVerified: boolean) {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/extraction-batches/${id}/verify/?advanced=true`,
      { is_verified: isVerified }
    );
  }
}

export default Extraction;
